
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock } from "lucide-react";
import { StudentWithAttendance } from "@/hooks/useReportsData";
import { LatenessChart } from "../charts/LatenessChart";
import { LatenessStatistics } from "./LatenessStatistics";

interface LatenessOverviewCardProps {
  filteredStudents: StudentWithAttendance[];
  selectedPeriod: string;
  lateRecords?: any[];
  periodDateRange?: { startDate: Date; endDate: Date };
}

export const LatenessOverviewCard = ({ 
  filteredStudents, 
  selectedPeriod, 
  lateRecords = [],
  periodDateRange 
}: LatenessOverviewCardProps) => {
  const latenessData = useMemo(() => {
    const totalStudents = filteredStudents.length;
    
    
    // إذا لم يكن هناك طلاب مفلترين، أرجع بيانات فارغة
    if (totalStudents === 0) {
      
      return {
        lateCount: 0,
        onTimeCount: 0,
        totalStudents: 0,
        totalLateInstances: 0,
        latePercentage: 0,
        onTimePercentage: 0
      };
    }
    
    // التأكد من أن lateDays رقم صحيح وليس null أو undefined
    const studentsWithValidLateDays = filteredStudents.map(s => {
      const lateDays = s.lateDays;
      let validLateDays = 0;
      
      if (typeof lateDays === 'number' && !isNaN(lateDays)) {
        validLateDays = Math.max(0, lateDays); // تأكد من عدم وجود قيم سالبة
      }
      
      return {
        ...s,
        lateDays: validLateDays
      };
    });
    
    // حساب الطلاب المتأخرين وغير المتأخرين بشكل صحيح
    const lateStudents = studentsWithValidLateDays.filter(s => s.lateDays > 0);
    const onTimeStudents = studentsWithValidLateDays.filter(s => s.lateDays === 0);
    
    const lateCount = lateStudents.length;
    const onTimeCount = onTimeStudents.length;
    
    // التأكد من أن المجموع يساوي إجمالي الطلاب
    const calculatedTotal = lateCount + onTimeCount;
    if (calculatedTotal !== totalStudents) {
      console.warn('Mismatch in student counts:', {
        totalStudents,
        lateCount,
        onTimeCount,
        calculatedTotal
      });
    }
    
    // حساب العدد الكلي لحالات التأخر
    const totalLateInstances = studentsWithValidLateDays.reduce((sum, s) => sum + s.lateDays, 0);
    
    const latePercentage = totalStudents > 0 ? Math.round((lateCount / totalStudents) * 100) : 0;
    const onTimePercentage = totalStudents > 0 ? Math.round((onTimeCount / totalStudents) * 100) : 0;


    return {
      lateCount,
      onTimeCount,
      totalStudents,
      totalLateInstances,
      latePercentage,
      onTimePercentage
    };
  }, [filteredStudents, lateRecords]);

  const getPeriodText = () => {
    switch (selectedPeriod) {
      case "weekly": return "أسبوعياً";
      case "monthly": return "شهرياً"; 
      case "quarterly": return "فصلياً";
      case "yearly": return "سنوياً";
      default: return "إجمالي";
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2 bg-secondary text-white rounded-t-lg">
        <CardTitle className="text-lg font-semibold flex items-center gap-2 text-white">
          <Clock className="h-5 w-5 text-white" />
          الرسم البياني للتأخر
        </CardTitle>
        <span className="text-sm text-white/80">{getPeriodText()}</span>
      </CardHeader>
      <CardContent className="p-6 pt-4">
        <div className="flex flex-col space-y-4">
          {/* الإحصائيات في الأعلى */}
          <LatenessStatistics latenessData={latenessData} />
          
          {/* الرسم البياني في الأسفل مع ارتفاع ثابت */}
          <div className="w-full">
            <LatenessChart
              selectedPeriod={selectedPeriod}
              lateRecords={lateRecords}
              filteredStudents={filteredStudents}
              periodDateRange={periodDateRange}
              latenessData={latenessData}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
